import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Link, Text, Box } from 'components/ui';
import { wrapFunctionalComponent } from 'utils/framework';
import deliveryFrequency from 'utils/DeliveryFrequency';
import { globalModals, renderModal } from 'utils/globalModals';
import { globalModalsSelector } from 'selectors/page/headerFooterTemplate/data/globalModals/globalModalsSelector';
import bccUtils from 'utils/BCC';
import Actions from 'actions/Actions';
import DateUtil from 'utils/Date';
import StringUtils from 'utils/String';
import languageLocale from 'utils/LanguageLocale';
import FulfillmentMethodsEN from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_en_US';
import FulfillmentMethodsFR from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_fr_CA';

const { AUTO_REPLENISH_PRODUCT_INFO } = globalModals;
const { AUTO_REPLENISHMENT } = bccUtils.MEDIA_IDS;
const { formatSavingAmountString, formatFrequencyType } = deliveryFrequency;
const getText = languageLocale.getLocalisedDataFromResource(FulfillmentMethodsEN, FulfillmentMethodsFR);

/**
 * Builds the accelerated-promotion ("Hot Deal") legal copy shown only when the
 * current SKU carries an `acceleratedPromotion`. Mirrors the legacy
 * `AutoReplenishment` component so the redesign keeps parity for that segment.
 */
const buildAcceleratedPromoCopy = currentSku => {
    const { acceleratedPromotion } = currentSku || {};
    const {
        childOrderCount, promotionDuration, promotionExpirationDate, baseReplenishmentAdjuster
    } = acceleratedPromotion || {};

    const promoDiscount = formatSavingAmountString(currentSku, null, true);
    const basePromoDiscount = `${Math.ceil(baseReplenishmentAdjuster)}%`;
    const promoExpDate = DateUtil.getDateInMDYFormat(promotionExpirationDate);

    return {
        legalCopyShown: StringUtils.format(
            getText('autoReplenishFirstTimeLegalShown', ['{0}', '{1}', '{2}', '{3}']),
            promoDiscount,
            childOrderCount,
            promotionDuration,
            promoExpDate
        ),
        legalMoreDetails: StringUtils.format(
            getText('autoReplenishFirstTimeLegalMoreDetails', ['{0}', '{1}', '{2}', '{3}']),
            childOrderCount,
            promoDiscount,
            promotionDuration,
            basePromoDiscount
        )
    };
};

/**
 * AutoReplenishDetails renders the expanded Auto-Replenish state for the SHP-5111
 * card-based fulfillment selector: the "Deliver every: ..." frequency selector,
 * accelerated-promotion legal copy (when applicable), the recurring-charge
 * consent line, and the "Learn more" link that opens the Auto-Replenish product
 * info modal.
 *
 * The discounted price with strikethrough original is rendered by the parent
 * `AutoReplenishRow` on the title row (right-aligned on desktop, stacked on
 * mobile) per the Figma layout, so it is intentionally not repeated here.
 *
 * It reads the shared Auto-Replenish state from `commerce` (from `useCommerce`),
 * so it stays in sync with the checkbox and the commerce/basket flow. This
 * mirrors the legacy `components/ProductPage/DeliveryOptions/AutoReplenishment`
 * eligible branch — no backend or breaking changes, UI only.
 *
 * @param {object} props
 * @param {object} props.currentSku - The current SKU (drives promo copy).
 * @param {object} props.commerce - Shared Auto-Replenish state from useCommerce.
 * @returns {JSX.Element}
 */
const AutoReplenishDetails = ({ currentSku, commerce = {} }) => {
    const dispatch = useDispatch();
    const reduxGlobalModals = useSelector(globalModalsSelector, shallowEqual);
    const [showPromoDetails, setShowPromoDetails] = useState(false);

    const {
        replenishmentFreqNum,
        replenishmentFreqType,
        isAutoReplenMostCommon,
        toggleDeliveryFrequencyModal
    } = commerce;

    const { acceleratedPromotion } = currentSku || {};

    const openAutoReplenishModal = () =>
        renderModal(reduxGlobalModals[AUTO_REPLENISH_PRODUCT_INFO], () =>
            dispatch(
                Actions.showMediaModal({
                    isOpen: true,
                    mediaId: AUTO_REPLENISHMENT,
                    titleDataAt: 'autoReplenishModalTitle'
                })
            )
        );

    const promoCopy = acceleratedPromotion ? buildAcceleratedPromoCopy(currentSku) : null;

    return (
        <Box>
            <Link
                onClick={() => toggleDeliveryFrequencyModal?.(true)}
                arrowDirection='down'
                fontSize='sm'
                padding={1}
                margin={-1}
                aria-label={getText('deliveryFrequencyLinkAriaLabel')}
            >
                {getText('deliveryEvery')}{' '}
                <Text
                    is='span'
                    fontSize='sm'
                    fontWeight='bold'
                >
                    {replenishmentFreqNum}&nbsp;
                    {formatFrequencyType(replenishmentFreqNum, replenishmentFreqType)}
                    {isAutoReplenMostCommon && ` (${getText('mostCommon')})`}
                </Text>
            </Link>
            {acceleratedPromotion && (
                <Text
                    is='p'
                    marginTop={2}
                    fontSize='sm'
                    color='gray'
                >
                    {`${promoCopy.legalCopyShown} `}
                    {!showPromoDetails && (
                        <Link
                            onClick={() => setShowPromoDetails(true)}
                            fontSize='sm'
                            color='blue'
                            children={getText('autoReplenishFirstTimeLegalDetails')}
                        />
                    )}
                </Text>
            )}
            {showPromoDetails && promoCopy && (
                <Text
                    is='p'
                    marginTop={2}
                    fontSize='sm'
                    color='gray'
                    children={promoCopy.legalMoreDetails}
                />
            )}
            <Text
                is='p'
                marginTop={2}
                fontSize='sm'
                color='gray'
                children={getText('autoReplenishLegalOptIn')}
            />
            <Text
                is='p'
                marginTop={2}
            >
                <Link
                    display='block'
                    color='blue'
                    fontSize='sm'
                    onClick={openAutoReplenishModal}
                    padding={2}
                    margin={-2}
                    children={getText('learnMore')}
                />
            </Text>
        </Box>
    );
};

AutoReplenishDetails.propTypes = {
    currentSku: PropTypes.object,
    commerce: PropTypes.object
};

export default wrapFunctionalComponent(AutoReplenishDetails, 'AutoReplenishDetails');
